import { useEffect, useMemo, useRef, useState } from 'react';
import Panel from '@/components/common/Panel';
import { useCurrency } from '@/context/CurrencyContext';
import { ResponsiveContainer, BarChart, XAxis, YAxis, CartesianGrid, Bar, Legend, Tooltip } from 'recharts';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { formatToShortMonth } from '@/lib/time_utils';

const BalanceBarChart = ({ data }) => {
    const { formatNumber } = useCurrency();
    const containerRef = useRef(null);
    const [chartHeight, setChartHeight] = useState(400);

    const chartData = useMemo(() => {
        const formatter = new Intl.DateTimeFormat(i18n.language, { month: 'short' });
        return data.months.map((monthStr: any, i: string | number) => {
            const date = new Date(`${monthStr}-01`);
            return {
                month: formatToShortMonth(formatter.format(date)),
                assets: data.assets.total[i] ?? 0,
                liabilities: data.liabilities.total[i] ?? 0,
                equity: data.equity.total[i] ?? 0
            };
        });
    }, [data]);

    useEffect(() => {
        const calculateHeight = () => {
            const topOffset = containerRef.current?.getBoundingClientRect().top || 0;
            const windowHeight = window.innerHeight;
            const reservedSpace = 40;
            const availableHeight = windowHeight - topOffset - reservedSpace;
            setChartHeight(Math.max(300, availableHeight));
        };

        calculateHeight();
        window.addEventListener('resize', calculateHeight);
        return () => window.removeEventListener('resize', calculateHeight);
    }, []);

    const { t } = useTranslation();
    return (
        <Panel className="p-6">
            <div ref={containerRef} style={{ height: chartHeight }}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 20, right: 20, left: 20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis tickFormatter={formatNumber} />
                        <Tooltip
                            formatter={(value: number) => formatNumber(value)}
                            labelFormatter={(label: string) => `${t("common:month")}: ${label}`}
                        />
                        <Legend />
                        <Bar dataKey="assets" name={t("balance_sheet:assets")} fill="#1E90FF" />
                        <Bar dataKey="liabilities" name={t("balance_sheet:liabilities")} fill="#FF4136" />
                        <Bar dataKey="equity" name={t("balance_sheet:equity")} fill="#2ECC40" />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </Panel>
    );
};

export default BalanceBarChart;
